// dashboard/frontend/src/components/ArmingConsole.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRuleActivations, putRuleMode, getArmingEntities, putArmingEntity,
  deleteArmingEntity, getArmingConfig, putArmingConfig,
  getArmingExclusive, putArmingExclusive,
} from '../services/api';
import ModeBadge, { runtimeModeView } from './ModeBadge';

const CAVEAT = "Armé=buy déclenche un ACHAT réel via l'exécution Rust (SP2, livrée) — si le sensor a ARMED_EXECUTION_ENABLED=on ET le master ARMÉ.";

export default function ArmingConsole() {
  const qc = useQueryClient();
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({
    kind: 'creator', address: '', size_mult: '', size_cap_sol: '',
    // gate d'entrée (§1.2a) — vide = aucune contrainte
    max_pct_above_genesis: '', max_progress: '', max_token_age_sec: '',
    // profil de sell (§1.2b) — vide = constantes tp_watcher actuelles
    crash_brutal_velocity_threshold: '', crash_brutal_grace_sec: '',
    tp_take_pct: '', trailing_pct: '',
  });

  const actQ = useQuery({ queryKey: ['disc-activations'], queryFn: getRuleActivations });
  const entQ = useQuery({ queryKey: ['arm-entities'], queryFn: getArmingEntities });
  const cfgQ = useQuery({ queryKey: ['arm-config'], queryFn: getArmingConfig });
  const exclQ = useQuery({ queryKey: ['arming-exclusive'], queryFn: getArmingExclusive });

  const cfg = cfgQ.data || { master_armed: false, max_concurrent_armed: 1 };
  // P7.489 : mode du moteur renvoyé par /api/arming/config (bot:mode > MODE env > unknown).
  // Jamais PAPER par défaut : « MODE ? » tant que non confirmé (chargement, erreur, inconnu).
  const modeView = runtimeModeView(cfgQ.data, { isError: cfgQ.isError });
  const exclusiveEnabled = exclQ.data?.enabled ?? false;
  const activations = actQ.data?.activations || [];
  const entities = entQ.data?.entities || [];

  const onErr = (e) => setErr(e?.response?.data?.detail || e.message);
  const mRuleMode = useMutation({ mutationFn: ({ id, mode }) => putRuleMode(id, mode),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['disc-activations'] }); }, onError: onErr });
  const mEnt = useMutation({ mutationFn: (b) => putArmingEntity(b),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['arm-entities'] }); }, onError: onErr });
  const mEntDel = useMutation({ mutationFn: (id) => deleteArmingEntity(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['arm-entities'] }) });
  const mCfg = useMutation({ mutationFn: (b) => putArmingConfig(b),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['arm-config'] }); }, onError: onErr });
  const mExcl = useMutation({ mutationFn: (enabled) => putArmingExclusive(enabled),
    onSuccess: () => { setErr(null); qc.invalidateQueries({ queryKey: ['arming-exclusive'] }); }, onError: onErr });

  const num = (v) => (v === '' || v == null ? null : Number(v));
  // tip_lamports non exposé dans le formulaire (posé via publish côté conteneur comme genesis_enabled).
  // Si l'adresse existe déjà, l'upsert écrase tip_lamports à NULL — comportement cohérent avec
  // genesis_enabled qui n'est pas non plus dans ce formulaire.
  const addEntity = () => mEnt.mutate({
    kind: form.kind, address: form.address.trim(), mode: 'observe',
    size_mult: num(form.size_mult), size_cap_sol: num(form.size_cap_sol),
    max_pct_above_genesis: num(form.max_pct_above_genesis),
    max_progress: num(form.max_progress),
    max_token_age_sec: num(form.max_token_age_sec),
    crash_brutal_velocity_threshold: num(form.crash_brutal_velocity_threshold),
    crash_brutal_grace_sec: num(form.crash_brutal_grace_sec),
    tp_take_pct: num(form.tp_take_pct),
    trailing_pct: num(form.trailing_pct),
  });

  return (
    <div className="cs-wrap">
      <div className="cs-caveat">
        {CAVEAT} · MODE courant : <ModeBadge view={modeView} inline />
        {modeView.isUnknown
          ? <> — mode non confirmé : un armé=buy peut acheter en <b>LIVE</b>.</>
          : <> — un armé=buy achètera en <b>{modeView.short}</b>.</>}
      </div>
      {err && <div className="cs-err">Erreur : {err}</div>}

      <div className="h1">Garde-fous globaux</div>
      <div className="arm-bar">
        <button className={`tdp-btn ${cfg.master_armed ? 'on' : ''}`}
          onClick={() => mCfg.mutate({ master_armed: !cfg.master_armed })}>
          {cfg.master_armed ? '● ARMÉ (master ON)' : '○ DÉSARMÉ (master OFF)'}
        </button>
        <span className="cs-slot">max positions concurrentes :</span>
        <input className="arm-in" type="number" min="0" defaultValue={cfg.max_concurrent_armed}
          onBlur={(e) => mCfg.mutate({ max_concurrent_armed: Number(e.target.value) })} />
      </div>

      <div className="arm-bar" style={{ marginTop: 10, alignItems: 'flex-start', gap: 12 }}>
        <button
          className={`tdp-btn ${exclusiveEnabled ? 'on' : ''}`}
          onClick={() => mExcl.mutate(!exclusiveEnabled)}
          disabled={mExcl.isPending}
          title="Mode armé-only"
        >
          {exclusiveEnabled ? '● Mode armé-only : ON' : '○ Mode armé-only : OFF'}
        </button>
        <span className="cs-slot" style={{ lineHeight: '1.4' }}>
          ON = seules les entités armées tradent (le buy par détection est coupé)
        </span>
      </div>

      <div className="h1" style={{ marginTop: 16 }}>Règles (mode)</div>
      <div className="cs-slot" style={{ marginBottom: 6 }}>Liste = activations (Config &amp; Shadow). Une règle minée jamais activée n'apparaît pas ici — l'activer d'abord dans Config &amp; Shadow.</div>
      {activations.length === 0 ? (
        <div className="tdp-empty">Aucune activation — activer des règles dans l'onglet Config &amp; Shadow.</div>
      ) : (
        <table className="disc-tbl">
          <thead><tr><th>Prédicat</th><th>Éval.</th><th>Actif</th><th>Mode</th><th>Action</th></tr></thead>
          <tbody>
            {activations.map((a) => (
              <tr key={a.id}>
                <td>{a.feature} {a.op} {a.threshold} <span className="cs-slot">[{a.slot_tag || '—'}]</span></td>
                <td><span className="cs-badge" data-ev={a.evaluability}>{a.evaluability}</span></td>
                <td>{a.active ? 'oui' : <span className="cs-slot">non</span>}</td>
                <td>{a.mode === 'buy' ? <b style={{ color: 'var(--red)' }}>buy</b> : (a.mode || 'observe')}</td>
                <td>
                  <button className="tdp-btn" disabled={a.evaluability !== 'live' || !a.active}
                    title={a.evaluability !== 'live'
                      ? 'Règle batch non-armable (feature inconnue à la décision live)'
                      : (!a.active ? 'Activation désactivée — réactiver dans Config & Shadow' : '')}
                    onClick={() => mRuleMode.mutate({ id: a.id, mode: a.mode === 'buy' ? 'observe' : 'buy' })}>
                    {a.mode === 'buy' ? '→ observe' : '→ buy'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="h1" style={{ marginTop: 16 }}>Entités trackées</div>
      <div className="arm-bar">
        <select className="arm-in" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          <option value="creator">creator</option><option value="wallet">wallet</option>
        </select>
        <input className="arm-in" placeholder="address" value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <input className="arm-in" placeholder="size_mult" value={form.size_mult}
          onChange={(e) => setForm({ ...form, size_mult: e.target.value })} />
        <input className="arm-in" placeholder="cap SOL" value={form.size_cap_sol}
          onChange={(e) => setForm({ ...form, size_cap_sol: e.target.value })} />
        <button className="tdp-btn on" onClick={addEntity} disabled={!form.address.trim()}>Ajouter</button>
      </div>

      <div className="h2" style={{ marginTop: 10, fontWeight: 600 }}>Gate d'entrée</div>
      <div className="cs-slot" style={{ marginBottom: 4 }}>
        Vide = aucune contrainte. Flags Rust : ARMED_ENTRY_GATE_MODE=shadow/enforce (défaut off).
      </div>
      <div className="arm-bar">
        <input className="arm-in" placeholder="max % au-dessus du launch (ex: 0.40)" value={form.max_pct_above_genesis}
          onChange={(e) => setForm({ ...form, max_pct_above_genesis: e.target.value })} />
        <input className="arm-in" placeholder="max progress courbe (ex: 0.15)" value={form.max_progress}
          onChange={(e) => setForm({ ...form, max_progress: e.target.value })} />
        <input className="arm-in" placeholder="max âge token (s) [curve/rule seulement]" value={form.max_token_age_sec}
          onChange={(e) => setForm({ ...form, max_token_age_sec: e.target.value })} />
      </div>

      <div className="h2" style={{ marginTop: 10, fontWeight: 600 }}>Profil de sortie</div>
      <div className="cs-slot" style={{ marginBottom: 4 }}>
        Vide = constantes tp_watcher actuelles. Flag Rust : ARMED_ENTITY_SELL_ENABLED (défaut off).
        Desserrer CRASH_BRUTAL réduit l'évitement-de-rug — activer sur preuve seulement (§7 spec).
      </div>
      <div className="arm-bar">
        <input className="arm-in" placeholder="CRASH_BRUTAL vélocité (%/s, ex: -0.70)" value={form.crash_brutal_velocity_threshold}
          onChange={(e) => setForm({ ...form, crash_brutal_velocity_threshold: e.target.value })} />
        <input className="arm-in" placeholder="grâce (s, ex: 1.5)" value={form.crash_brutal_grace_sec}
          onChange={(e) => setForm({ ...form, crash_brutal_grace_sec: e.target.value })} />
        <input className="arm-in" placeholder="TP take % (ex: 0.30)" value={form.tp_take_pct}
          onChange={(e) => setForm({ ...form, tp_take_pct: e.target.value })} />
        <input className="arm-in" placeholder="trailing % (ex: 0.10)" value={form.trailing_pct}
          onChange={(e) => setForm({ ...form, trailing_pct: e.target.value })} />
      </div>
      <table className="disc-tbl">
        <thead><tr><th>Kind</th><th>Adresse</th><th>Nature</th><th>Mode</th><th>Action</th></tr></thead>
        <tbody>
          {entities.filter((e) => e.active).map((e) => (
            <tr key={e.id}>
              <td>{e.kind}</td>
              <td className="cs-slot">{e.address.slice(0, 6)}…{e.address.slice(-4)}</td>
              <td>{e.reactive ? <span className="cs-drift">réactif (après eux)</span> : 'slot-0'}</td>
              <td>{e.mode}</td>
              <td>
                <button className="tdp-btn"
                  onClick={() => mEnt.mutate({
                    kind: e.kind, address: e.address,
                    mode: e.mode === 'buy' ? 'observe' : 'buy',
                    size_mult: e.size_mult, size_cap_sol: e.size_cap_sol,
                    max_pct_above_genesis: e.max_pct_above_genesis,
                    max_progress: e.max_progress,
                    max_token_age_sec: e.max_token_age_sec,
                    crash_brutal_velocity_threshold: e.crash_brutal_velocity_threshold,
                    crash_brutal_grace_sec: e.crash_brutal_grace_sec,
                    tp_take_pct: e.tp_take_pct,
                    trailing_pct: e.trailing_pct,
                    genesis_enabled: e.genesis_enabled,
                    tip_lamports: e.tip_lamports,
                  })}>
                  {e.mode === 'buy' ? '→ observe' : '→ buy'}
                </button>
                <button className="tdp-btn" onClick={() => mEntDel.mutate(e.id)}>suppr</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
